import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useUser from '../auth/useUser';
import { useMsal } from '@azure/msal-react';
import SupplierInfoModal from '../components/modals/SupplierInfoModal';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import './Suppliers.css';

const API = import.meta.env.VITE_API_URL;

async function getAuthHeaders(msalInstance) {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) return {};
    const resp = await msalInstance.acquireTokenSilent({
      scopes: [`${import.meta.env.VITE_AZURE_CLIENT_ID}/.default`],
      account: accounts[0],
    });
    return { Authorization: `Bearer ${resp.idToken}` };
  } catch {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0 && accounts[0].idToken) {
      return { Authorization: `Bearer ${accounts[0].idToken}` };
    }
    return {};
  }
}

function statusBadgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'approved') return 'green';
  if (s === 'pending') return 'amber';
  if (s === 'suspended') return 'red';
  return 'neutral';
}

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : n);

const PIE_COLORS = { Approved: '#10b981', Pending: '#f59e0b', Rejected: '#ef4444', 'No Status': '#9ca3af' };
const CLICKABLE_STATUSES = new Set(['Approved', 'Pending', 'Rejected']);

function renderCustomTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0];
  const pct = d.payload.total > 0 ? ((d.value / d.payload.total) * 100).toFixed(1) : '0.0';
  return (
    <div style={{ background: 'var(--card)', border: '1px solid #dee2e6', borderRadius: 8, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,.1)' }}>
      <div style={{ fontWeight: 600, color: d.payload.fill }}>{d.name}</div>
      <div>{fmt(d.value)} products ({pct}%)</div>
    </div>
  );
}

function RenderLegend({ payload, onClickStatus }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
      {payload.map((entry, i) => {
        const clickable = onClickStatus && CLICKABLE_STATUSES.has(entry.value);
        return (
          <div
            key={i}
            onClick={() => { if (clickable) onClickStatus(entry.value); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: clickable ? 'pointer' : 'default', borderRadius: 4, padding: '2px 4px' }}
            onMouseEnter={(e) => { if (clickable) e.currentTarget.style.background = '#f3f4f6'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}
          >
            <span style={{ width: 12, height: 12, borderRadius: 3, background: entry.color, flexShrink: 0 }} />
            <span style={{ fontWeight: 500 }}>{entry.value}{entry.value === 'No Status' ? ' (uncategorized)' : ''}</span>
            <span style={{ color: 'var(--t3)', marginLeft: 'auto' }}>{fmt(entry.payload.value)}</span>
          </div>
        );
      })}
    </div>
  );
}

const PAGE_SIZE = 20;

export default function Profile() {
  const { displayName, email, initials } = useUser();
  const { instance: msalInstance } = useMsal();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [suppliersTotal, setSuppliersTotal] = useState(0);
  const [suppliersPage, setSuppliersPage] = useState(1);
  const [suppliersLoading, setSuppliersLoading] = useState(true);
  const [productStatus, setProductStatus] = useState(null);
  const [openRow, setOpenRow] = useState(null);
  const [toast, setToast] = useState(null);
  const [allSupplierNames, setAllSupplierNames] = useState(null);
  const [todos, setTodos] = useState(null);
  const [todoPage, setTodoPage] = useState(1);

  // Fetch stats
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const auth = await getAuthHeaders(msalInstance);
        const r = await fetch(`${API}/api/profile/stats`, { headers: auth });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) setStats(d);
      } catch {
        if (alive) setStatsErr(true);
      }
    })();
    return () => { alive = false; };
  }, [msalInstance]);

  // Fetch products by status
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const auth = await getAuthHeaders(msalInstance);
        const r = await fetch(`${API}/api/profile/products-by-status`, { headers: auth });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) setProductStatus(d);
      } catch {}
    })();
    return () => { alive = false; };
  }, [msalInstance]);

  // Fetch assigned suppliers
  const loadSuppliers = useCallback(async (pg) => {
    setSuppliersLoading(true);
    try {
      const auth = await getAuthHeaders(msalInstance);
      const r = await fetch(`${API}/api/profile/assigned-suppliers?page=${pg}&limit=${PAGE_SIZE}`, { headers: auth });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setSuppliers(d.rows || []);
      setSuppliersTotal(d.total || 0);
    } catch {
      setSuppliers([]);
      setSuppliersTotal(0);
    } finally {
      setSuppliersLoading(false);
    }
  }, [msalInstance]);

  useEffect(() => { loadSuppliers(suppliersPage); }, [suppliersPage, loadSuppliers]);

  // Fetch ALL assigned supplier names (for pie click navigation)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const auth = await getAuthHeaders(msalInstance);
        const r = await fetch(`${API}/api/profile/assigned-suppliers?names_only=true`, { headers: auth });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) setAllSupplierNames(d.supplier_names || []);
      } catch {}
    })();
    return () => { alive = false; };
  }, [msalInstance]);

  // Fetch to-dos
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const auth = await getAuthHeaders(msalInstance);
        const r = await fetch(`${API}/api/profile/todos`, { headers: auth });
        if (!r.ok) throw new Error();
        const d = await r.json();
        if (alive) setTodos(d);
      } catch {}
    })();
    return () => { alive = false; };
  }, [msalInstance]);

  const handlePieClick = useCallback((status) => {
    if (!allSupplierNames || allSupplierNames.length === 0) return;
    const supplierParam = allSupplierNames.join(',');
    const params = new URLSearchParams();
    params.set('supplier', supplierParam);
    params.set('product_status', status);
    navigate(`/supplier-products?${params.toString()}`);
  }, [allSupplierNames, navigate]);

  const renderKpiVal = (v) => {
    if (statsErr) return '—';
    if (stats == null) return <span className="kpi-skel" aria-hidden="true" />;
    return fmt(v);
  };

  const pageCount = Math.max(1, Math.ceil(suppliersTotal / PAGE_SIZE));
  const safePage = Math.min(suppliersPage, pageCount);

  // Pie data
  const pieData = productStatus && productStatus.total > 0
    ? [
        { name: 'Approved',  key: 'approved' },
        { name: 'Pending',   key: 'pending' },
        { name: 'Rejected',  key: 'rejected' },
        { name: 'No Status', key: 'no_status' },
      ]
        .filter(s => (productStatus[s.key] || 0) > 0)
        .map(s => ({ name: s.name, value: productStatus[s.key], fill: PIE_COLORS[s.name], total: productStatus.total }))
    : [];

  return (
    <div className="content">
      {/* ── Hero band ── */}
      <div className="d-flex align-items-center gap-4 p-4 rounded-4 flex-wrap" style={{ background: 'linear-gradient(135deg,#0D2B3E,#1e4f6b)', color: '#fff' }}>
        <div className="d-flex align-items-center justify-content-center" style={{ width: 72, height: 72, borderRadius: 18, background: 'linear-gradient(135deg,var(--teal),var(--teal2))', fontSize: 26, fontWeight: 800, flexShrink: 0, color: '#ffffff' }}>{initials}</div>
        <div className="flex-grow-1">
          <div style={{ fontSize: 30, fontWeight: 800, color: '#ffffff', letterSpacing: '-0.5px' }}>{displayName}</div>
          <div style={{ fontSize: '12.5px', color: 'rgba(255,255,255,0.65)', marginTop: 3 }}>Focus Care Pharma</div>
          <div className="d-flex gap-2 mt-2 flex-wrap">
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{email}</span>
          </div>
        </div>
        <div className="d-flex gap-3 flex-wrap" style={{ alignItems: 'flex-end' }}>
          {[
            { val: renderKpiVal(stats?.assigned_suppliers), lbl: 'Assigned Suppliers' },
            { val: renderKpiVal(stats?.assigned_supplier_products), lbl: 'Assigned Supplier Products' },
            { val: renderKpiVal(stats?.uploaded_supplier_files), lbl: 'Uploaded Supplier Files' },
          ].map((s, i) => (
            <div key={i} className="text-center p-3 rounded-3 flex-grow-1" style={{ background: 'rgba(255,255,255,.08)', minWidth: 80 }}>
              <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 24, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}>{s.val}</div>
              <div style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, fontSize: '10.5px', opacity: .5, marginTop: 2, letterSpacing: '0.8px', textTransform: 'uppercase' }}>{s.lbl}</div>
            </div>
          ))}
          <div
            className="text-center p-3 rounded-3 flex-grow-1"
            style={{ background: 'rgba(255,255,255,.08)', minWidth: 80, cursor: 'pointer' }}
            onClick={() => navigate('/chat-history')}
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter') navigate('/chat-history'); }}
          >
            <div style={{ fontWeight: 800, fontSize: 24, letterSpacing: '-1px' }}>💬</div>
            <div style={{ fontWeight: 600, fontSize: '10.5px', opacity: .5, marginTop: 2, letterSpacing: '0.8px', textTransform: 'uppercase' }}>Chat History</div>
          </div>
        </div>
      </div>

      {/* ── To-Do List ── */}
      <div className="row g-3" style={{ marginTop: 4 }}>
        <div className="col-12">
          <div className="card">
            <div className="ch"><div className="ch-ico">📋</div><div><div className="ch-title" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 800, fontSize: 22, color: 'var(--navy)' }}>My To-Do</div></div></div>
            {todos == null ? (
              <div className="cb d-flex align-items-center justify-content-center" style={{ color: 'var(--t3)', fontSize: 13, padding: 24 }}>Loading...</div>
            ) : todos.total_count === 0 ? (
              <div className="cb d-flex align-items-center justify-content-center" style={{ color: 'var(--t3)', fontSize: 13, padding: 24 }}>All caught up! There is no to-do. Enjoy!</div>
            ) : (() => {
              const TODO_PAGE = 5;
              const todoRows = todos.rows || [];
              const todoPgCount = Math.max(1, Math.ceil(todoRows.length / TODO_PAGE));
              const safeTodoPg = Math.min(todoPage, todoPgCount);
              const todoStart = (safeTodoPg - 1) * TODO_PAGE;
              const todoSlice = todoRows.slice(todoStart, todoStart + TODO_PAGE);
              return (
                <>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={{ width: 60, padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 14, fontWeight: 700 }}>Task</th>
                          <th style={{ width: 100, padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 14, fontWeight: 700 }}>Action</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', color: 'var(--navy)', fontSize: 14, fontWeight: 700 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {todoSlice.map((row, idx) => {
                          const num = todoStart + idx + 1;
                          const desc = row.status_type === 'Pending'
                            ? `There are ${fmt(row.product_count)} supplier products in Pending status from ${row.supplier_name}. Please assign the proper status.`
                            : `There are ${fmt(row.product_count)} uncategorized supplier products from ${row.supplier_name}. Please assign the proper status.`;
                          const statusParam = row.status_type === 'Pending' ? 'Pending' : 'No Status';
                          const params = new URLSearchParams();
                          params.set('supplier', row.supplier_name);
                          params.set('product_status', statusParam);
                          return (
                            <tr key={`${row.supplier_name}-${row.status_type}`} style={{ transition: 'background 0.15s' }} onMouseEnter={e => { for (const td of e.currentTarget.children) td.style.background = '#EFF8FF'; }} onMouseLeave={e => { for (const td of e.currentTarget.children) td.style.background = ''; }}>
                              <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--navy)', fontSize: 15 }}>{num}</td>
                              <td style={{ padding: '10px 12px' }}>
                                <button type="button" className="btn btn-sm" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white' }} title="Open in supplier products" onClick={() => navigate(`/supplier-products?${params.toString()}`)}>
                                  <i className="bi bi-box-arrow-up-right" style={{ color: 'var(--navy)', fontSize: 14 }}></i>
                                </button>
                              </td>
                              <td style={{ padding: '10px 12px', fontSize: 15, color: 'var(--navy)' }}>{desc}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {todoPgCount > 1 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', flexWrap: 'nowrap' }}>
                      <nav aria-label="todo-pagination" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        <button type="button" disabled={safeTodoPg === 1} onClick={() => setTodoPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safeTodoPg === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safeTodoPg === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safeTodoPg === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
                        {(() => {
                          const win = 5;
                          let s = Math.max(1, safeTodoPg - Math.floor(win / 2));
                          let e = Math.min(todoPgCount, s + win - 1);
                          s = Math.max(1, e - win + 1);
                          const nums = [];
                          for (let i = s; i <= e; i++) nums.push(i);
                          return nums.map(n => (
                            <button key={n} type="button" onClick={() => setTodoPage(n)} style={{ fontSize: 12, color: safeTodoPg === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safeTodoPg === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safeTodoPg === n ? 600 : 400 }}>{n}</button>
                          ));
                        })()}
                        <button type="button" disabled={safeTodoPg === todoPgCount} onClick={() => setTodoPage(p => Math.min(todoPgCount, p + 1))} style={{ fontSize: 12, color: safeTodoPg === todoPgCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safeTodoPg === todoPgCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safeTodoPg === todoPgCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
                      </nav>
                      <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                        {todoStart + 1}–{Math.min(todoStart + TODO_PAGE, todoRows.length)} of {todoRows.length}
                      </span>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ── Two-column body ── */}
      <div className="row g-3" style={{ marginTop: 4 }}>
        {/* LEFT — Suppliers Assigned to Me */}
        <div className="col-lg-7">
          <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="ch"><div className="ch-ico">🤝</div><div><div className="ch-title" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>Suppliers Assigned to Me</div></div></div>
            {suppliersLoading && suppliers.length === 0 ? (
              <div className="cb d-flex align-items-center justify-content-center" style={{ flex: 1, color: 'var(--t3)', fontSize: 13 }}>Loading...</div>
            ) : suppliers.length === 0 ? (
              <div className="cb d-flex align-items-center justify-content-center" style={{ flex: 1, color: 'var(--t3)', fontSize: 13 }}>No suppliers assigned to you yet.</div>
            ) : (
              <>
                <div className="table-responsive" style={{ flex: 1 }}>
                  <table className="sup-table" style={{ width: '100%' }}>
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>Open</th>
                        <th>Supplier Name</th>
                        <th>Supplier Status</th>
                        <th>Full Name</th>
                        <th>Phone</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suppliers.map(r => (
                        <tr key={r.supplier_id || r.supplier_name}>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm"
                              style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 8px', background: 'white' }}
                              title="Open supplier"
                              onClick={() => setOpenRow(r)}
                            >
                              <i className="bi bi-box-arrow-up-right" style={{ color: 'var(--teal)', fontSize: 14 }}></i>
                            </button>
                          </td>
                          <td><span className="sup-prod-name">{r.supplier_name || '—'}</span></td>
                          <td>
                            <span className={`sup-badge ${statusBadgeClass(r.supplier_approval_status)}`}>
                              {r.supplier_approval_status || '—'}
                            </span>
                          </td>
                          <td>{r.first_full_name || '—'}</td>
                          <td>{r.first_phone || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {pageCount > 1 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderTop: '1px solid #eef3f7', flexWrap: 'nowrap' }}>
                    <nav aria-label="pagination" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                      <button type="button" disabled={safePage === 1} onClick={() => setSuppliersPage(p => Math.max(1, p - 1))} style={{ fontSize: 12, color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === 1 ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === 1 ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Previous</button>
                      {(() => {
                        const win = 5;
                        let s = Math.max(1, safePage - Math.floor(win / 2));
                        let e = Math.min(pageCount, s + win - 1);
                        s = Math.max(1, e - win + 1);
                        const nums = [];
                        for (let i = s; i <= e; i++) nums.push(i);
                        return nums.map(n => (
                          <button key={n} type="button" onClick={() => setSuppliersPage(n)} style={{ fontSize: 12, color: safePage === n ? 'white' : 'var(--teal)', border: '1px solid var(--teal)', background: safePage === n ? 'var(--teal)' : 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: 'pointer', whiteSpace: 'nowrap', fontWeight: safePage === n ? 600 : 400 }}>{n}</button>
                        ));
                      })()}
                      <button type="button" disabled={safePage === pageCount} onClick={() => setSuppliersPage(p => Math.min(pageCount, p + 1))} style={{ fontSize: 12, color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safePage === pageCount ? '#dee2e6' : 'var(--teal)'), background: 'var(--card)', borderRadius: 6, padding: '5px 10px', cursor: safePage === pageCount ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>Next</button>
                    </nav>
                    <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--t3)', whiteSpace: 'nowrap' }}>
                      Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, suppliersTotal)} of {fmt(suppliersTotal)}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* RIGHT — Products Assigned to Me (Pie Chart) */}
        <div className="col-lg-5">
          <div className="card" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="ch"><div className="ch-ico">💊</div><div><div className="ch-title" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 700 }}>Products Assigned to Me</div></div></div>
            <div className="cb d-flex align-items-center justify-content-center" style={{ flex: 1, minHeight: 300 }}>
              {productStatus == null ? (
                <span style={{ color: 'var(--t3)', fontSize: 13 }}>Loading...</span>
              ) : pieData.length === 0 ? (
                <span style={{ color: 'var(--t3)', fontSize: 13 }}>No products to display.</span>
              ) : (
                <ResponsiveContainer width="100%" height={320}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="45%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                      animationDuration={800}
                      animationEasing="ease-out"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      onClick={(data) => { if (data && data.name && CLICKABLE_STATUSES.has(data.name)) handlePieClick(data.name); }}
                    >
                      {pieData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} style={{ cursor: CLICKABLE_STATUSES.has(entry.name) ? 'pointer' : 'default' }} />
                      ))}
                    </Pie>
                    <Tooltip content={renderCustomTooltip} />
                    <Legend content={(props) => <RenderLegend {...props} onClickStatus={handlePieClick} />} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Supplier Info Modal ── */}
      {openRow && (
        <SupplierInfoModal
          supplierId={openRow.supplier_id}
          supplierName={openRow.supplier_name}
          onClose={() => setOpenRow(null)}
          onSaved={() => loadSuppliers(suppliersPage)}
          onToast={setToast}
        />
      )}

      {toast && (
        <div className={`sup-toast ${toast.type === 'error' ? 'error' : ''}`}>{toast.msg}</div>
      )}
    </div>
  );
}
